import { Field as FormField, FieldGroup, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { dropNulls } from '../../lib/api'
import type { AmendmentLog } from '../../lib/types'
import { ChoiceButtons, SameAsLast } from '../chips'
import { amountProblem, optNum, optText, seasonYearOf, str, type FlowDef } from '../flow'
import { AmountInput, lastFieldLog, placeOf } from './shared'
import { namesUsed, rememberWho, WhoField } from '../who'

const SUBSTANCE_TYPES = ['poultry manure', 'cattle manure', 'hog manure', 'compost', 'biosolids', 'mulch', 'row cover', 'other'] as const

export const amendmentFlow: FlowDef = {
  kind: 'amendment',
  checkForm: 'h2Amendment',
  label: 'Soil amendment',
  needsField: true,
  severalFields: true,
  initial: ({ today, fieldId }) => ({ fieldId, date: today, rateUnit: 't/ha' }),
  fetchLast: (orgId, farmId, fieldId) => lastFieldLog<AmendmentLog>('amendment-logs', orgId, farmId, fieldId),
  steps: [
    {
      id: 'what',
      title: 'What did you put on?',
      valid: (v) => str(v.substance).trim() !== '' && !amountProblem(v.rateAmount),
      render: (ctx) => {
        const { values, patch, last } = ctx
        const l = last as AmendmentLog | null
        return (
          <FieldGroup>
            {l && (
              <SameAsLast
                label={[l.substance, l.supplierName].filter(Boolean).join(', ')}
                onClick={() =>
                  patch({
                    substance: l.substance,
                    substanceType: l.substanceType ?? '',
                    supplier: l.supplierName ?? '',
                    rateAmount: l.rateAmount == null ? '' : String(l.rateAmount),
                    rateUnit: l.rateUnit ?? 't/ha',
                    applicator: l.applicatorName ?? '',
                  })
                }
              />
            )}
            <ChoiceButtons label="Kind"
                value={str(values.substanceType) as (typeof SUBSTANCE_TYPES)[number] | ''}
                options={SUBSTANCE_TYPES.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
                onChange={(t) => patch({ substanceType: t })}
              />
            <FormField>
              <Label>What exactly?</Label>
              <Input value={str(values.substance)} onChange={(e) => patch({ substance: e.target.value })} placeholder="e.g. Composted chicken manure" autoComplete="off" />
            </FormField>
            <FormField>
              <Label>Supplier</Label>
              <Input value={str(values.supplier)} onChange={(e) => patch({ supplier: e.target.value })} autoComplete="off" />
            </FormField>
            <div className="flex gap-3">
              <FormField className="flex-1">
                <Label>Rate</Label>
                <AmountInput value={values.rateAmount} onChange={(r) => patch({ rateAmount: r })} />
              </FormField>
              <FormField className="w-28">
                <Label>Per</Label>
                <Input value={str(values.rateUnit)} onChange={(e) => patch({ rateUnit: e.target.value })} />
              </FormField>
            </div>
            <FormField>
              <Label>Earliest safe harvest date</Label>
              <Input type="date" value={str(values.eahd)} onChange={(e) => patch({ eahd: e.target.value })} />
            </FormField>
            <WhoField ctx={ctx} />
          </FieldGroup>
        )
      },
    },
  ],
  review: ({ values, selectedFields }) => ({
    title: `Soil amendment${placeOf({ selectedFields })}`,
    lines: [
      { label: 'Date', value: str(values.date) },
      { label: 'Applied', value: [str(values.substance), optText(values.substanceType)].filter(Boolean).join(' · ') },
      ...(optText(values.supplier) ? [{ label: 'Supplier', value: str(values.supplier) }] : []),
      ...(optNum(values.rateAmount) != null ? [{ label: 'Rate', value: `${str(values.rateAmount)} ${str(values.rateUnit)}` }] : []),
      ...(optText(values.eahd) ? [{ label: 'Safe to harvest from', value: str(values.eahd) }] : []),
      ...(namesUsed(values).length ? [{ label: 'Applied by', value: [...new Set(namesUsed(values))].join(', ') }] : []),
    ],
  }),
  entry: (ctx) => {
    const { values } = ctx
    const date = str(values.date)
    const rate = optNum(values.rateAmount)
    return dropNulls({
      fieldId: str(values.fieldId),
      seasonYear: seasonYearOf(date),
      appliedOn: date,
      substance: str(values.substance).trim(),
      substanceType: optText(values.substanceType) ?? null,
      supplierName: optText(values.supplier) ?? null,
      rateAmount: rate ?? null,
      rateUnit: rate == null ? null : (optText(values.rateUnit) ?? null),
      eahd: optText(values.eahd) ?? null,
      applicatorName: optText(values.applicator) ?? null,
    })
  },
  badge: { framework: 'CanadaGAP', formCode: 'H2', formName: 'Agronomic Inputs (Soil Amendments)' },
  historyPath: (farmId, fieldId) => `/farms/${farmId}/fields/${fieldId}/amendment-logs`,
  onRecorded: rememberWho(),
}
